import { useCallback, useEffect, useRef, useState } from "react";

export default function useChecklistTemplates(enabled, getChecklists) {
  const [checklistTemplates, setTemplates] = useState([]);
  const [loadingChecklists, setLoading] = useState(false);
  const page = useRef(0);
  const hasMore = useRef(true);
  const pending = useRef(false);
  const version = useRef(0);
  const list = useRef(getChecklists);
  list.current = getChecklists;

  const fetchPage = useCallback(async (nextPage, reset = false) => {
    if (!enabled || pending.current) return;
    const requestVersion = version.current;
    pending.current = true;
    setLoading(true);
    try {
      const result = await list.current({ page: nextPage, per_page: 20 });
      if (requestVersion !== version.current) return;
      const templates = result?.checklists || [];
      setTemplates((previous) => reset ? templates : [...previous, ...templates]);
      page.current = nextPage;
      hasMore.current = templates.length === 20;
    } catch (error) {
      if (requestVersion === version.current) {
        console.error("Error fetching checklist templates:", error);
      }
    } finally {
      if (requestVersion === version.current) {
        pending.current = false;
        setLoading(false);
      }
    }
  }, [enabled]);

  useEffect(() => {
    setTemplates([]);
    page.current = 0;
    hasMore.current = true;
    pending.current = false;
    setLoading(false);
    if (enabled) fetchPage(0, true);
    return () => { version.current += 1; };
  }, [enabled, fetchPage]);

  const handleChecklistScroll = (event) => {
    const { target } = event;
    if (enabled && hasMore.current &&
        target.scrollHeight - target.scrollTop <= target.clientHeight) {
      fetchPage(page.current + 1);
    }
  };

  return { checklistTemplates, loadingChecklists, handleChecklistScroll };
}
